import { palette } from '../palette';
import {
  BACKGROUND_DISABLED,
  BLUE_PRIMARY,
  BORDER_PRIMARY,
  DARK_0,
  DARK_3,
  DARK_4,
  DARK_5,
  DARK_7,
  FOREGROUND_DISABLED,
  FOREGROUND_PRIMARY,
  FOREGROUND_SECONDARY,
  LIGHT_0,
  LIGHT_5,
  LIGHT_BACKGROUND,
} from './tokens';

import type { PaletteMode, PaletteOptions } from '@mui/material';

/**
 * The dark half of the Design System 2.0 palette (HNS-263).
 *
 * The legacy palette barely distinguishes the two modes — it flips `text.primary` to white and stops
 * there, leaving `background.paper` at #fff and `divider` at `grey[200]`. That was survivable while
 * the only dark surfaces were action bars holding white icons, but a dark PANEL needs the mode to
 * mean something: its children read `background.paper`, `divider` and `text.secondary` off the theme
 * and would otherwise render a light card inside a dark frame.
 *
 * The ramp below is `Color/Dark/*` from `design-system.css`. Only the panel background (`Dark/3`) is
 * specified by design; the rest is EXTRAPOLATED one step at a time up the ramp — nested surfaces at
 * `Dark/4`, borders at `Dark/5` — and wants a designer's confirmation.
 */
const darkPalette = (base: Partial<PaletteOptions>): Partial<PaletteOptions> => ({
  ...base,
  background: { default: DARK_0, paper: DARK_3 },
  text: { primary: LIGHT_0, secondary: LIGHT_5, disabled: DARK_7 },
  divider: DARK_5,
  action: { active: LIGHT_0, disabled: DARK_7, disabledBackground: DARK_4 },
  // Mirrors the DS 2.0 secondary button on dark (`.dark .ds-btn-secondary-default`): a `Dark/4` fill
  // that lifts to `Dark/5` on hover.
  secondary: { main: DARK_4, dark: DARK_5, contrastText: LIGHT_0 },
});

/**
 * The light-mode text, divider and action roles, moved off the legacy `grey` ramp onto the DS 2.0
 * semantic roles (NTL-219). Every MUI `Typography`, `Divider` and disabled control reads these, so
 * this repaints the text and edges of every surface not yet swapped to a DS 2.0 component. Each
 * value is the role the replacing DS 2.0 component draws (the DS Card's copy and edge, the DS
 * Table's row text), or failing that the role Figma describes for the job.
 *
 * Two calls worth knowing: `text.disabled` gets visibly darker, because the library sets disabled
 * text at 2.81:1 on purpose; and `divider` is the solid `border/primary` rather than the DS
 * Divider's translucent `border/on-surface`, because MUI reads `divider` for borders as well as rules.
 *
 * The `grey` ramp itself is left alone: many files read `grey[N]` directly, for purposes this block
 * cannot see, so remapping the ramp would recolour them blind. The deprecated `custom.lightGrey` is
 * remapped, since every reader uses it as a border.
 */
const lightRoles = (base: Partial<PaletteOptions>): Partial<PaletteOptions> => ({
  text: { primary: FOREGROUND_PRIMARY, secondary: FOREGROUND_SECONDARY, disabled: FOREGROUND_DISABLED },
  divider: BORDER_PRIMARY,
  action: { active: FOREGROUND_SECONDARY, disabled: FOREGROUND_DISABLED, disabledBackground: BACKGROUND_DISABLED },
  // The legacy `palette()` always sets `custom`; `Partial` only makes it look optional here.
  custom: { ...(base.custom as NonNullable<PaletteOptions['custom']>), lightGrey: BORDER_PRIMARY },
});

/**
 * The Design System 2.0 palette (HNS-259).
 *
 * In light mode the accent, the page background and the text, divider and action roles move (see
 * `lightRoles`). The `grey`, `blue` and status ramps are still the legacy ones.
 *
 *   - `primary.main` — `Color/Blue/Primary`, the accent DS 2.0 draws every emphasised control with,
 *     replacing `blue[600]` (#1570ef). This is the widest-reaching line in the refresh: MUI derives
 *     `light`, `dark` and `contrastText` from it, and roughly 76 files read it for links, icons,
 *     selected states, focus rings, progress bars and switches. Setting it here rather than
 *     repainting each control is the point — a component override per surface would leave the app
 *     two shades of blue for as long as any surface was missed.
 *   - `background.default` — from `grey[25]` (#fcfcfd, near enough white to read as white) to
 *     `Color/Light/Background` (#f9fafa), which is what every DS 2.0 screen sits on. Figma node
 *     18187:17351.
 *
 * `primary` is still honoured when a caller passes one, so the per-account branding seam the
 * signature implies survives the refresh — though nothing in the app passes it today.
 *
 * `background.paper` deliberately stays white in light mode: the Figma card is white ON the tinted
 * page, and that contrast is the whole point of the token.
 */
export const refreshPalette = (primary?: string, mode: PaletteMode = 'light'): Partial<PaletteOptions> => {
  const base = { ...palette(primary, mode), primary: { main: primary ?? BLUE_PRIMARY } };

  if (mode === 'dark') return darkPalette(base);

  return {
    ...base,
    background: { ...base.background, default: LIGHT_BACKGROUND },
    ...lightRoles(base),
  };
};
